/**
 * IT Service Management Section
 * @description IT 서비스 요청 관리 - catalogs/itsm-services.json 및 systems.json CRUD
 */

import { useState, useEffect } from 'react';
import {
  Badge, Button, Card, Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, Input, Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, Textarea,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@shared/ui/primitives';
import { Plus, Pencil, Trash2, Settings, Database } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/utils/httpClient';

// ITSM Services 타입 정의
interface ITSMService {
    serviceId: string;
    displayName: string;
    description: string;
    ticketModel: {
        ticketType: string;
        recommendedStatusValues: string[];
        terminalStatuses: string[];
    };
    integrationHints?: {
        [key: string]: string[];
    };
}

interface ITSMServicesConfig {
    schemaVersion: string;
    tenantId: string;
    services: ITSMService[];
    extensibility?: any;
}

// Systems 타입 정의
interface System {
    systemId: string;
    displayName: string;
    kind: 'infra' | 'system' | 'portal';
    ownerTeamId: string;
    supportedTicketTypes: string[];
    ui: {
        serviceId: string;
    };
}

interface SystemsConfig {
    schemaVersion: string;
    tenantId: string;
    systems: System[];
    notes?: any;
}

export const ITServicePage: React.FC = () => {
    const { user } = useAuthStore();
    const [itsmServices, setItsmServices] = useState<ITSMService[]>([]);
    const [systems, setSystems] = useState<System[]>([]);
    const [activeTab, setActiveTab] = useState<'services' | 'systems'>('services');
    const [tenantId, setTenantId] = useState<string>('');
    const [tenantOptions, setTenantOptions] = useState<Array<{ tenantId: string; tenantName: string }>>([]);

    // Dialog states
    const [isServiceDialogOpen, setIsServiceDialogOpen] = useState(false);
    const [isSystemDialogOpen, setIsSystemDialogOpen] = useState(false);
    const [editingServiceIndex, setEditingServiceIndex] = useState<number | null>(null);
    const [editingSystemIndex, setEditingSystemIndex] = useState<number | null>(null);

    // Form states
    const [serviceFormData, setServiceFormData] = useState<ITSMService>({
        serviceId: '',
        displayName: '',
        description: '',
        ticketModel: {
            ticketType: '',
            recommendedStatusValues: [],
            terminalStatuses: ['closed'],
        },
    });

    const [systemFormData, setSystemFormData] = useState<System>({
        systemId: '',
        displayName: '',
        kind: 'system',
        ownerTeamId: '',
        supportedTicketTypes: [],
        ui: {
            serviceId: '',
        },
    });

    const [statusValuesInput, setStatusValuesInput] = useState('');
    const [terminalStatusesInput, setTerminalStatusesInput] = useState('closed');
    const [supportedTicketTypesInput, setSupportedTicketTypesInput] = useState('');

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // tenant-user는 자신의 tenantId 사용
        if (user?.userType === 'tenant-user' && user.tenantId) {
            setTenantId(user.tenantId);
            return;
        }

        // portal-admin은 tenant 선택이 필요 → tenant 목록 조회 후 기본값 선택
        if (user?.userType === 'portal-admin') {
            (async () => {
                try {
                    const data = await apiClient.get<{ tenants: Array<{ tenantId: string; tenantName: string }> }>('/admin/tenants');
                    const tenants = data.tenants || [];
                    setTenantOptions(tenants);

                    const savedTenantId = localStorage.getItem('iampam_admin_selected_tenantId') || '';
                    const initialTenantId =
                        (savedTenantId && tenants.some(t => t.tenantId === savedTenantId) && savedTenantId) ||
                        tenants[0]?.tenantId ||
                        '';
                    setTenantId(initialTenantId);
                } catch (error) {
                    console.error('Failed to load tenant list:', error);
                    setTenantOptions([]);
                    setTenantId('');
                }
            })();
        }
    }, [user?.userType, (user as any)?.tenantId]);

    useEffect(() => {
        if (!tenantId) return;
        if (user?.userType === 'portal-admin') {
            localStorage.setItem('iampam_admin_selected_tenantId', tenantId);
        }
        loadData(tenantId);
    }, [tenantId]);

    const loadData = async (targetTenantId: string) => {
        try {
            setIsLoading(true);

            // Load ITSM Services
            try {
                const itsmData = await apiClient.get<ITSMServicesConfig>(
                    `/admin/tenants/${encodeURIComponent(targetTenantId)}/catalogs/itsm-services`
                );
                setItsmServices(itsmData.services || []);
            } catch (err: any) {
                // 파일이 없을 수 있음(초기 테넌트) → 빈 목록으로 시작
                if (err?.status === 404) setItsmServices([]);
                else throw err;
            }

            // Load Systems
            try {
                const systemsData = await apiClient.get<SystemsConfig>(
                    `/admin/tenants/${encodeURIComponent(targetTenantId)}/catalogs/systems`
                );
                setSystems(systemsData.systems || []);
            } catch (err: any) {
                if (err?.status === 404) setSystems([]);
                else throw err;
            }
        } catch (error) {
            console.error('Error loading catalog data:', error);
            // 최소한 UI가 비지 않도록 빈 상태 유지 (mock 데이터는 운영 혼선 방지 위해 제거)
            setItsmServices([]);
            setSystems([]);
        } finally {
            setIsLoading(false);
        }
    };

    const saveItsmServices = async (newServices: ITSMService[]) => {
        setIsLoading(true);
        try {
            if (!tenantId) throw new Error('tenantId가 선택되지 않았습니다.');

            const config: ITSMServicesConfig = {
                schemaVersion: '2.0.0',
                tenantId,
                services: newServices,
            };

            await apiClient.put(
                `/admin/tenants/${encodeURIComponent(tenantId)}/catalogs/itsm-services`,
                config
            );

            setItsmServices(newServices);
            alert('ITSM 서비스가 저장되었습니다.');
        } catch (error) {
            console.error('Error saving ITSM services:', error);
            alert('저장 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const saveSystems = async (newSystems: System[]) => {
        setIsLoading(true);
        try {
            if (!tenantId) throw new Error('tenantId가 선택되지 않았습니다.');

            const config: SystemsConfig = {
                schemaVersion: '2.0.0',
                tenantId,
                systems: newSystems,
            };

            await apiClient.put(
                `/admin/tenants/${encodeURIComponent(tenantId)}/catalogs/systems`,
                config
            );

            setSystems(newSystems);
            alert('시스템이 저장되었습니다.');
        } catch (error) {
            console.error('Error saving systems:', error);
            alert('저장 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    // ITSM Service handlers
    const handleAddService = () => {
        setEditingServiceIndex(null);
        setServiceFormData({
            serviceId: '',
            displayName: '',
            description: '',
            ticketModel: {
                ticketType: '',
                recommendedStatusValues: [],
                terminalStatuses: ['closed'],
            },
        });
        setStatusValuesInput('');
        setTerminalStatusesInput('closed');
        setIsServiceDialogOpen(true);
    };

    const handleEditService = (index: number) => {
        const service = itsmServices[index];
        setEditingServiceIndex(index);
        setServiceFormData({ ...service });
        setStatusValuesInput(service.ticketModel.recommendedStatusValues.join(', '));
        setTerminalStatusesInput(service.ticketModel.terminalStatuses.join(', '));
        setIsServiceDialogOpen(true);
    };

    const handleDeleteService = (index: number) => {
        if (confirm('정말 삭제하시겠습니까?')) {
            const newServices = itsmServices.filter((_, i) => i !== index);
            saveItsmServices(newServices);
        }
    };

    const handleSaveService = () => {
        if (!serviceFormData.serviceId.trim() || !serviceFormData.displayName.trim()) {
            alert('필수 필드를 입력해주세요.');
            return;
        }

        const statusValues = statusValuesInput
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0);

        const terminalStatuses = terminalStatusesInput
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0);

        const updatedService: ITSMService = {
            ...serviceFormData,
            serviceId: serviceFormData.serviceId.trim(),
            displayName: serviceFormData.displayName.trim(),
            description: serviceFormData.description.trim(),
            ticketModel: {
                ...serviceFormData.ticketModel,
                ticketType: serviceFormData.serviceId.trim(),
                recommendedStatusValues: statusValues,
                terminalStatuses: terminalStatuses,
            },
        };

        let newServices: ITSMService[];
        if (editingServiceIndex !== null) {
            newServices = itsmServices.map((s, i) => (i === editingServiceIndex ? updatedService : s));
        } else {
            newServices = [...itsmServices, updatedService];
        }

        saveItsmServices(newServices);
        setIsServiceDialogOpen(false);
    };

    // System handlers
    const handleAddSystem = () => {
        setEditingSystemIndex(null);
        setSystemFormData({
            systemId: '',
            displayName: '',
            kind: 'system',
            ownerTeamId: '',
            supportedTicketTypes: [],
            ui: { serviceId: '' },
        });
        setSupportedTicketTypesInput('');
        setIsSystemDialogOpen(true);
    };

    const handleEditSystem = (index: number) => {
        const system = systems[index];
        setEditingSystemIndex(index);
        setSystemFormData({ ...system });
        setSupportedTicketTypesInput(system.supportedTicketTypes.join(', '));
        setIsSystemDialogOpen(true);
    };

    const handleDeleteSystem = (index: number) => {
        if (confirm('정말 삭제하시겠습니까?')) {
            const newSystems = systems.filter((_, i) => i !== index);
            saveSystems(newSystems);
        }
    };

    const handleSaveSystem = () => {
        if (!systemFormData.systemId.trim() || !systemFormData.displayName.trim()) {
            alert('필수 필드를 입력해주세요.');
            return;
        }

        const supportedTypes = supportedTicketTypesInput
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0);

        const updatedSystem: System = {
            ...systemFormData,
            systemId: systemFormData.systemId.trim(),
            displayName: systemFormData.displayName.trim(),
            ownerTeamId: systemFormData.ownerTeamId.trim(),
            supportedTicketTypes: supportedTypes,
            ui: {
                serviceId: systemFormData.ui.serviceId.trim() || systemFormData.systemId.trim(),
            },
        };

        let newSystems: System[];
        if (editingSystemIndex !== null) {
            newSystems = systems.map((s, i) => (i === editingSystemIndex ? updatedSystem : s));
        } else {
            newSystems = [...systems, updatedSystem];
        }

        saveSystems(newSystems);
        setIsSystemDialogOpen(false);
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-foreground">IT 서비스 요청 관리</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        ITSM 서비스와 시스템 카탈로그를 관리합니다.
                    </p>
                </div>
                {user?.userType === 'portal-admin' && (
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Tenant</span>
                        <Select value={tenantId} onValueChange={(v) => setTenantId(v)}>
                            <SelectTrigger className="w-[220px]">
                                <SelectValue placeholder="테넌트 선택" />
                            </SelectTrigger>
                            <SelectContent>
                                {tenantOptions.map((t) => (
                                    <SelectItem key={t.tenantId} value={t.tenantId}>
                                        {t.tenantName} ({t.tenantId})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>

            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'services' | 'systems')}>
                <TabsList>
                    <TabsTrigger value="services" className="gap-2">
                        <Settings className="w-4 h-4" />
                        ITSM 서비스
                    </TabsTrigger>
                    <TabsTrigger value="systems" className="gap-2">
                        <Database className="w-4 h-4" />
                        시스템
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="services" className="space-y-4">
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-semibold">ITSM 서비스 (itsm-services.json)</h3>
                                <p className="text-sm text-muted-foreground">
                                    서비스 요청, 장애 관리, 변경 관리 등의 ITSM 서비스를 정의합니다.
                                </p>
                            </div>
                            <Button onClick={handleAddService}>
                                <Plus className="w-4 h-4 mr-2" />
                                서비스 추가
                            </Button>
                        </div>

                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>서비스 ID</TableHead>
                                    <TableHead>표시명</TableHead>
                                    <TableHead>설명</TableHead>
                                    <TableHead>상태값</TableHead>
                                    <TableHead>종료 상태</TableHead>
                                    <TableHead className="text-right">작업</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {itsmServices.map((service, index) => (
                                    <TableRow key={service.serviceId}>
                                        <TableCell className="font-medium">{service.serviceId}</TableCell>
                                        <TableCell>{service.displayName}</TableCell>
                                        <TableCell className="max-w-xs truncate">{service.description}</TableCell>
                                        <TableCell>
                                            <div className="flex gap-1 flex-wrap">
                                                {service.ticketModel.recommendedStatusValues.slice(0, 3).map((status) => (
                                                    <Badge key={status} variant="outline" className="text-xs">
                                                        {status}
                                                    </Badge>
                                                ))}
                                                {service.ticketModel.recommendedStatusValues.length > 3 && (
                                                    <Badge variant="outline" className="text-xs">
                                                        +{service.ticketModel.recommendedStatusValues.length - 3}
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                {service.ticketModel.terminalStatuses.map((status) => (
                                                    <Badge key={status} variant="secondary" className="text-xs">
                                                        {status}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button variant="outline" size="sm" onClick={() => handleEditService(index)}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button variant="outline" size="sm" onClick={() => handleDeleteService(index)}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>

                <TabsContent value="systems" className="space-y-4">
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-semibold">시스템 (systems.json)</h3>
                                <p className="text-sm text-muted-foreground">
                                    클라우드, 온프레미스 등의 시스템과 지원하는 티켓 유형을 정의합니다.
                                </p>
                            </div>
                            <Button onClick={handleAddSystem}>
                                <Plus className="w-4 h-4 mr-2" />
                                시스템 추가
                            </Button>
                        </div>

                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>시스템 ID</TableHead>
                                    <TableHead>표시명</TableHead>
                                    <TableHead>종류</TableHead>
                                    <TableHead>소유 팀</TableHead>
                                    <TableHead>지원 티켓 유형</TableHead>
                                    <TableHead className="text-right">작업</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {systems.map((system, index) => (
                                    <TableRow key={system.systemId}>
                                        <TableCell className="font-medium">{system.systemId}</TableCell>
                                        <TableCell>{system.displayName}</TableCell>
                                        <TableCell>
                                            <Badge variant={system.kind === 'infra' ? 'default' : system.kind === 'portal' ? 'secondary' : 'outline'}>
                                                {system.kind}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{system.ownerTeamId}</TableCell>
                                        <TableCell>
                                            <div className="flex gap-1 flex-wrap">
                                                {system.supportedTicketTypes.map((type) => (
                                                    <Badge key={type} variant="outline" className="text-xs">
                                                        {type}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button variant="outline" size="sm" onClick={() => handleEditSystem(index)}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button variant="outline" size="sm" onClick={() => handleDeleteSystem(index)}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* ITSM Service Dialog */}
            <Dialog open={isServiceDialogOpen} onOpenChange={setIsServiceDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editingServiceIndex !== null ? 'ITSM 서비스 수정' : 'ITSM 서비스 추가'}
                        </DialogTitle>
                        <DialogDescription>
                            ITSM 서비스의 정보와 티켓 모델을 설정합니다.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">서비스 ID *</label>
                            <Input
                                value={serviceFormData.serviceId}
                                onChange={(e) => setServiceFormData({ ...serviceFormData, serviceId: e.target.value })}
                                placeholder="예: servicerequest"
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">표시명 *</label>
                            <Input
                                value={serviceFormData.displayName}
                                onChange={(e) => setServiceFormData({ ...serviceFormData, displayName: e.target.value })}
                                placeholder="예: 서비스 요청"
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">설명</label>
                            <Textarea
                                value={serviceFormData.description}
                                onChange={(e) => setServiceFormData({ ...serviceFormData, description: e.target.value })}
                                placeholder="서비스 설명을 입력하세요"
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">권장 상태값 (쉼표로 구분)</label>
                            <Input
                                value={statusValuesInput}
                                onChange={(e) => setStatusValuesInput(e.target.value)}
                                placeholder="예: new, assigned, in_progress, closed"
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">종료 상태 (쉼표로 구분)</label>
                            <Input
                                value={terminalStatusesInput}
                                onChange={(e) => setTerminalStatusesInput(e.target.value)}
                                placeholder="예: closed"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsServiceDialogOpen(false)}>
                            취소
                        </Button>
                        <Button onClick={handleSaveService} disabled={isLoading}>
                            {isLoading ? '저장 중...' : '저장'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* System Dialog */}
            <Dialog open={isSystemDialogOpen} onOpenChange={setIsSystemDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editingSystemIndex !== null ? '시스템 수정' : '시스템 추가'}
                        </DialogTitle>
                        <DialogDescription>
                            시스템 정보와 지원하는 티켓 유형을 설정합니다.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">시스템 ID *</label>
                            <Input
                                value={systemFormData.systemId}
                                onChange={(e) => setSystemFormData({ ...systemFormData, systemId: e.target.value })}
                                placeholder="예: cloud_services"
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">표시명 *</label>
                            <Input
                                value={systemFormData.displayName}
                                onChange={(e) => setSystemFormData({ ...systemFormData, displayName: e.target.value })}
                                placeholder="예: 클라우드 서비스"
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">종류</label>
                            <select
                                value={systemFormData.kind}
                                onChange={(e) => setSystemFormData({ ...systemFormData, kind: e.target.value as 'infra' | 'system' | 'portal' })}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                <option value="infra">인프라</option>
                                <option value="system">시스템</option>
                                <option value="portal">포털</option>
                            </select>
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">소유 팀 ID</label>
                            <Input
                                value={systemFormData.ownerTeamId}
                                onChange={(e) => setSystemFormData({ ...systemFormData, ownerTeamId: e.target.value })}
                                placeholder="예: operations"
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">지원 티켓 유형 (쉼표로 구분)</label>
                            <Input
                                value={supportedTicketTypesInput}
                                onChange={(e) => setSupportedTicketTypesInput(e.target.value)}
                                placeholder="예: servicerequest, incident, changerequest"
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">UI 서비스 ID</label>
                            <Input
                                value={systemFormData.ui.serviceId}
                                onChange={(e) => setSystemFormData({
                                    ...systemFormData,
                                    ui: { ...systemFormData.ui, serviceId: e.target.value }
                                })}
                                placeholder="시스템 ID와 동일하게 설정됩니다"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsSystemDialogOpen(false)}>
                            취소
                        </Button>
                        <Button onClick={handleSaveSystem} disabled={isLoading}>
                            {isLoading ? '저장 중...' : '저장'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

ITServicePage.displayName = 'ITServicePage';